import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * SeasonTimeline — 32-dot horizontal timeline showing where we are in the
 * current season. Colours:
 *   • green   → NATIONAL_SEASON (Day 1-29)
 *   • yellow  → NATIONAL_FREEZE (Day 30)
 *   • gold    → INTERNATIONAL / Championship (Day 31)
 *   • gray    → TRANSITION (Day 32+, admin hasn't started next season yet)
 * Current day pulses to signal "you are here".
 */
export default function SeasonTimeline() {
  const [season, setSeason] = useState(null);
  useEffect(() => {
    api.get("/season/current").then((r) => setSeason(r.data)).catch(() => {});
  }, []);
  if (!season) return null;

  const day = season.day;
  const total = season.total_days || 32;
  const dots = Array.from({ length: total }, (_, i) => i + 1);

  const colorFor = (d) => {
    if (d <= 29) return "bg-nk-good";
    if (d === 30) return "bg-nk-warn";
    if (d === 31) return "bg-nk-gold";
    return "bg-nk-mute";
  };
  const labelFor = (d) => {
    if (d <= 29) return "Regular season";
    if (d === 30) return "Freeze day";
    if (d === 31) return "Championship";
    return "Transition";
  };

  return (
    <div className="nk-card p-4" data-testid="season-timeline">
      <div className="flex items-baseline justify-between mb-3">
        <p className="eyebrow">Season {season.season_number} · Day {day} / {total}</p>
        <p className="text-xs text-nk-mute">{labelFor(day)}</p>
      </div>
      <div className="flex items-center gap-[3px] overflow-hidden">
        {dots.map((d) => {
          const isPast = d < day;
          const isCurrent = d === day;
          const base = colorFor(d);
          return (
            <div
              key={d}
              title={`Day ${d} — ${labelFor(d)}`}
              className={`flex-1 h-1.5 rounded-full transition-all ${
                isCurrent ? `${base} shadow-[0_0_8px] shadow-current animate-pulse-dot h-2` :
                isPast    ? `${base} opacity-70` :
                            "bg-nk-line"
              }`}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3 text-[10px] uppercase tracking-wider text-nk-mute">
        <span className="flex items-center gap-1"><Dot cls="bg-nk-good" /> Play</span>
        <span className="flex items-center gap-1"><Dot cls="bg-nk-warn" /> Freeze</span>
        <span className="flex items-center gap-1"><Dot cls="bg-nk-gold" /> Championship</span>
        <span className="flex items-center gap-1"><Dot cls="bg-nk-mute" /> Transition</span>
      </div>
    </div>
  );
}

function Dot({ cls }) {
  return <span className={`w-1.5 h-1.5 rounded-full ${cls}`} />;
}
